const SUPPORTED = 'SUPPORTED';
const RESTRICTED = 'RESTRICTED';
const OUTDATED = 'OUTDATED';

export const browserSupportStatus = {
  SUPPORTED, // WebRTC should be working fine.
  RESTRICTED, // WebRTC is disabled.
  OUTDATED, // Browser version doesn't support WebRTC well enough.
};

const MIN_CHROME_VERSION = 69;
const MIN_EDGE_VERSION = 80;
const MIN_FIREFOX_VERSION = 78;
const MIN_SAFARI_VERSION = 15;

export const SUPPORTED_BROWSER_VERSIONS = {
  chrome: MIN_CHROME_VERSION,
  edge: MIN_EDGE_VERSION,
  firefox: MIN_FIREFOX_VERSION,
  safari: MIN_SAFARI_VERSION,
};

/**
 * @returns {String}
 */
export const checkBrowser = () => {
  if ((browserDetails.browser === 'chrome' && browserDetails.version >= MIN_CHROME_VERSION) ||
    (browserDetails.browser === 'edge' && browserDetails.version >= MIN_EDGE_VERSION) ||
    (browserDetails.browser === 'firefox' && browserDetails.version >= MIN_FIREFOX_VERSION && browserDetails.os !== 'iOS' && browserDetails.os !== 'Android') ||
    (browserDetails.browser === 'safari' && browserDetails.version >= MIN_SAFARI_VERSION) ||
    localStorage.getItem('rtc_allow_browser')
  ) {
    if (browserDetails.webRTCsupport) {
      return SUPPORTED;
    }
    return RESTRICTED;
  }
  return OUTDATED;
};

/**
 * Return the supported browser list according to the OS.
 */
export const getOsSupportedBrowsers = () => {
  const allSupportedBrowserList = Object.keys(SUPPORTED_BROWSER_VERSIONS);
  const isOsSupportedBrowser = (browserName) => allSupportedBrowserList.includes(browserName);
  switch (browserDetails.os) {
    case 'Android':
      return ['chrome'].filter(isOsSupportedBrowser);
    case 'iOS':
      return ['chrome', 'safari'].filter(isOsSupportedBrowser);
    case 'Linux':
      return ['chrome', 'firefox'].filter(isOsSupportedBrowser);
    case 'MacOS':
      return ['chrome', 'firefox', 'safari'].filter(isOsSupportedBrowser);
    case 'Windows':
      return ['chrome', 'edge', 'firefox'].filter(isOsSupportedBrowser);
    default:
      return ['chrome', 'edge', 'firefox', 'safari'].filter(isOsSupportedBrowser);
  }
};

// From https://stackoverflow.com/questions/38241480/detect-macos-ios-windows-android-and-linux-os-with-js
/**
 * @param window
 * @returns {null|String}
 */
const getOS = (window) => {
  const userAgent = window.navigator.userAgent;
  const platform = window.navigator.platform;
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
  const isIPad = navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1;

  if (macosPlatforms.indexOf(platform) !== -1) {
    return 'MacOS';
  } else if (isIPad || iosPlatforms.indexOf(platform) !== -1) {
    return 'iOS';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    return 'Windows';
  } else if (/Android/.test(userAgent)) {
    return 'Android';
  } else if (/Linux/.test(platform)) {
    return 'Linux';
  }
};

/**
 * Browser detector.
 */
export function detectBrowser(window) {
  const { navigator, RTCPeerConnection } = window;

  // Fail early if it's not a browser.
  if (typeof window === 'undefined' || !window.navigator) {
    const result = { browser: null, version: null };
    result.browser = 'Not a browser.';
    return result;
  }

  // User agent sniffing inspired by https://dev.to/shwetadanej/detect-browser-and-its-version-in-javascript-dg9
  const navUserAgent = navigator.userAgent;
  let browserName = navigator.appName;
  let browserVersion = '' + parseFloat(navigator.appVersion);
  let tempNameOffset, tempVersionOffset, tempVersion;

  if ((navUserAgent.indexOf('Opera')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('Opera');
    browserName = 'Opera';
    browserVersion = navUserAgent.substring(tempVersionOffset + 6);
    if ((navUserAgent.indexOf('Version')) !== -1) {
      tempVersionOffset = navUserAgent.indexOf('Version');
      browserVersion = navUserAgent.substring(tempVersionOffset + 8);
    }
  } else if ((navUserAgent.indexOf('MSIE')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('MSIE');
    browserName = 'Microsoft Internet Explorer';
    browserVersion = navUserAgent.substring(tempVersionOffset + 5);
  } else if ((navUserAgent.indexOf('Edg')) !== -1) {
    if (navUserAgent.indexOf('Edge') !== -1) {
      tempVersionOffset = navUserAgent.indexOf('Edge') + 5;
    } else {
      tempVersionOffset = navUserAgent.indexOf('Edg') + 4;
    }
    browserName = 'Edge';
    browserVersion = navUserAgent.substring(tempVersionOffset);
  } else if ((navUserAgent.indexOf('Chrome')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('Chrome');
    browserName = 'Chrome';
    browserVersion = navUserAgent.substring(tempVersionOffset + 7);
  } else if ((navUserAgent.indexOf('CriOS')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('CriOS');
    browserName = 'Chrome';
    browserVersion = navUserAgent.substring(tempVersionOffset + 6);
  } else if ((navUserAgent.indexOf('FxiOS')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('FxiOS');
    browserName = 'Firefox';
    browserVersion = navUserAgent.substring(tempVersionOffset + 6);
  } else if ((navUserAgent.indexOf('Safari')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('Safari');
    browserName = 'Safari';
    browserVersion = navUserAgent.substring(tempVersionOffset + 7);
    if ((navUserAgent.indexOf('Version')) !== -1) {
      tempVersionOffset = navUserAgent.indexOf('Version');
      browserVersion = navUserAgent.substring(tempVersionOffset + 8);
    }
  } else if ((navUserAgent.indexOf('Firefox')) !== -1) {
    tempVersionOffset = navUserAgent.indexOf('Firefox');
    browserName = 'Firefox';
    browserVersion = navUserAgent.substring(tempVersionOffset + 8);
  } else if ((tempNameOffset = navUserAgent.lastIndexOf(' ') + 1) < (tempVersionOffset = navUserAgent.lastIndexOf('/'))) {
    browserName = navUserAgent.substring(tempNameOffset, tempVersionOffset);
    browserVersion = navUserAgent.substring(tempVersionOffset + 1);
    if (browserName.toLowerCase() === browserName.toUpperCase()) {
      browserName = navigator.appName;
    }
  }

  // Trim version.
  if ((tempVersion = browserVersion.indexOf(';')) !== -1) {
    browserVersion = browserVersion.substring(0, tempVersion);
  }
  if ((tempVersion = browserVersion.indexOf(' ')) !== -1) {
    browserVersion = browserVersion.substring(0, tempVersion);
  }

  return {
    browser: browserName.toLowerCase(),
    fullVersion: browserVersion,
    version: browserVersion.split('.')[0],
    webRTCsupport: typeof navigator.mediaDevices === 'object' &&
      typeof navigator.mediaDevices.getUserMedia === 'function' &&
      typeof RTCPeerConnection === 'function',
    os: getOS(window),
  };
}

export const browserDetails = detectBrowser(window);
